import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { APPROVAL_BINARY_OPTIONS } from '../constants/common';
import { useGetDictionaries } from '../queries/data';
import { getFormattedCountry } from '../utils/common';

import { DICTIONARY_TYPE, useGetDictionaryNaming } from './useGetDictionaryNaming';

interface IFields {
  isFilter?: boolean;
  value: string;
}

export const useGetFieldNormalizer = () => {
  const { i18n, t } = useTranslation();

  const getDictionaryNaming = useGetDictionaryNaming();
  const getAnalyticDictionaryNaming = useGetDictionaryNaming({
    dictionaryType: DICTIONARY_TYPE.ANALYTICS,
  });
  const { data: appData } = useGetDictionaries();

  const normalizer = useMemo(() => {
    const cardBrandNetwork = ({ isFilter, value }: IFields) => {
      const result = value
        ?.split(':')
        .map((cardBrand) => getDictionaryNaming('cardBrand', cardBrand));

      if (isFilter) return { label: result.toString(), value };
      return result;
    };

    const cardTypes = ({ isFilter, value }: IFields) => {
      const result = value?.split(':');

      if (isFilter) return { label: result.toString(), value };
      return result;
    };

    const country = ({ isFilter, value }: IFields) => {
      const label = value
        ?.split(':')
        .map((countryCode) => getFormattedCountry(countryCode));

      if (isFilter) return { label: label.toString(), value };
      return label;
    };

    const transactionTypes = ({ isFilter, value }: IFields) => {
      const label = getDictionaryNaming('transactionTypes', value);
      if (isFilter) return { label, value };
      return label;
    };

    const paymentMethods = ({ isFilter, value }: IFields) => {
      const label = appData?.paymentMethodMap[value] || value;
      if (isFilter) return { label, value };
      return label;
    };

    const status = ({ isFilter, value }: IFields) => {
      const label = getDictionaryNaming('transactionState', value);
      if (isFilter) return { label, value };
      return label;
    };

    const compositeGroup = ({ isFilter, value }: IFields) => {
      const operation = value.substring(0, 2);
      const group = value.substring(3);

      const operationName = getDictionaryNaming('operations', operation);

      const label = `${operationName} ${group}`;
      if (isFilter) return { label, value };
      return label;
    };

    const binaryOptions = ({ isFilter, value }: IFields) => {
      const { label } = APPROVAL_BINARY_OPTIONS.filter(
        (option) => option.value === value,
      )[0];

      const result = i18n.exists(label) ? t(label as never) : label;

      if (isFilter) return { label: result, value };
      return result;
    };

    return {
      affiliateIdOperation: ({ isFilter, value }: IFields) => {
        const key = `cashierLimits.form.affiliateIdOperations.${value}`;
        const label = i18n.exists(key) ? t(key as never) : value;

        if (isFilter) return { label, value };
        return label;
      },
      antifraudProvider: ({ isFilter, value }: IFields) => {
        const label = getDictionaryNaming('antifraudProvider', value);
        if (isFilter) return { label, value };
        return label;
      },
      'bankTerminal.bank.paymentMethod': paymentMethods,
      binCountry: country,
      binRangeOperation: ({ isFilter, value }: IFields) => {
        const label = getDictionaryNaming('operations', value);
        if (isFilter) return { label, value };
        return label;
      },
      cardBrand: cardBrandNetwork,
      cardNetwork: cardBrandNetwork,
      cardType: cardTypes,
      compositeBinCountry: ({ isFilter, value }: IFields) => {
        const [operation, countryCode] = value.split(' ');

        const operationName = getDictionaryNaming('operations', operation);

        const label = `${operationName} ${getFormattedCountry(countryCode)}`;

        if (isFilter) return { label, value };
        return label;
      },
      compositeBinCountryGroup: compositeGroup,
      compositeBinGroup: compositeGroup,
      compositeCustomerCountry: ({ isFilter, value }: IFields) => {
        const [operation, countryCode] = value.split(' ');

        const operationName = getDictionaryNaming('operations', operation);

        const label = `${operationName} ${getFormattedCountry(countryCode)}`;

        if (isFilter) return { label, value };
        return label;
      },
      compositeCustomerCountryGroup: compositeGroup,
      compositeCustomerIp: ({ isFilter, value }: IFields) => {
        const [operation, countryCodes] = value.split(' ');

        const operationName = getDictionaryNaming('operations', operation);

        const result = countryCodes?.split(':').map((code) => getFormattedCountry(code));

        const label = `${operationName} ${result.toString()}`;

        if (isFilter) return { label, value };
        return { operation: operationName, values: result };
      },
      country,
      customerCountry: country,
      customerIdNormalization: ({ isFilter, value }: IFields) => {
        const label = getDictionaryNaming('customerIdNormalization', value);
        if (isFilter) return { label, value };
        return label;
      },
      isMoto: binaryOptions,
      isTrusted: binaryOptions,
      operationState: status,
      operationType: ({ isFilter, value }: IFields) => {
        const label = getDictionaryNaming('operationType', value);

        if (isFilter) return { label, value };
        return label;
      },
      paymentMethod: paymentMethods,
      paymentType: transactionTypes,
      resultCode: ({ isFilter, value }: IFields) => {
        const resultCode = appData?.resultCodeMap[value];

        const label = resultCode ? `${resultCode?.code} ${resultCode?.message}` : value;

        if (isFilter) return { label, value };
        return label;
      },
      state: status,
      transactionCountry: country,
      transactionState: status,
      transactionStatus: ({ isFilter, value }: IFields) => {
        const label = getAnalyticDictionaryNaming('transactionStatuses', value);

        if (isFilter) return { label, value };
        return label;
      },
      transactionType: transactionTypes,
      triggerType: ({ isFilter, value }: IFields) => {
        const label = getDictionaryNaming('mtCaTriggerType', value);

        if (isFilter) return { label, value };
        return label;
      },
      txCurrency: ({ isFilter, value }: IFields) => {
        const result = value?.split(':');

        if (isFilter) return { label: result.toString(), value };
        return result;
      },
      type: transactionTypes,
    };
  }, [appData, getAnalyticDictionaryNaming, getDictionaryNaming, i18n, t]);

  return { normalizer };
};
